import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  Close,
  ExpandMore,
  Refresh,
  RestaurantMenuOutlined,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { importIfoodCatalog, previewIfoodCatalog } from '../../../../services/api';
import { getIfoodErrorMessage } from '../../../../utils/ifoodError';

const IFOOD_RED = '#EA1D2C';

const money = (value) => Number(value || 0).toLocaleString('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const STATUS = {
  NEW: { label: 'Novo', color: 'success' },
  UPDATE: { label: 'Alterado no iFood', color: 'warning' },
  UNCHANGED: { label: 'Sem alterações', color: 'default' },
};

const getGroupRuleLabel = (group) => {
  const min = Math.max(0, Number(group?.min) || 0);
  const max = Number(group?.max) > 0 ? Number(group.max) : null;
  if (min > 0 && max === min) return `Escolha ${min}`;
  if (min > 0 && max) return `Escolha de ${min} a ${max}`;
  if (min > 0) return `Escolha no mínimo ${min}`;
  if (max) return `Escolha até ${max}`;
  return 'Opcional';
};

const IfoodImportItem = ({ item, selected, onToggle }) => {
  const status = STATUS[item.sync_status] || STATUS.UPDATE;
  const groups = Array.isArray(item.option_groups) ? item.option_groups : [];
  const hasGroups = groups.length > 0;

  const summary = (
    <Stack direction="row" alignItems="center" spacing={1.5} width="100%" minWidth={0}>
      <Checkbox
        checked={selected}
        onClick={(event) => event.stopPropagation()}
        onChange={() => onToggle(item.id)}
        inputProps={{ 'aria-label': `Selecionar ${item.name}` }}
      />
      <Avatar
        src={item.image_url || undefined}
        variant="rounded"
        sx={{ width: 54, height: 54, flexShrink: 0, bgcolor: 'grey.100', color: 'text.secondary' }}
      >
        <RestaurantMenuOutlined />
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography fontWeight={800} noWrap>{item.name}</Typography>
        <Typography variant="body2" color="text.secondary">
          {money(item.price)}{item.type === 'PIZZA' ? ' · Pizza' : ''}
        </Typography>
        {hasGroups && (
          <Typography variant="caption" color="text.secondary">
            {groups.length} {groups.length === 1 ? 'grupo de opções' : 'grupos de opções'}
          </Typography>
        )}
      </Box>
      <Chip label={status.label} color={status.color} size="small" sx={{ flexShrink: 0 }} />
    </Stack>
  );

  if (!hasGroups) {
    return <Box sx={{ py: 1.25 }}>{summary}</Box>;
  }

  return (
    <Accordion
      disableGutters
      elevation={0}
      TransitionProps={{ unmountOnExit: true }}
      sx={{
        bgcolor: 'transparent',
        '&::before': { display: 'none' },
        '&.Mui-expanded': { my: 0 },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        aria-controls={`ifood-item-${item.id}-groups`}
        id={`ifood-item-${item.id}`}
        sx={{
          px: 0,
          py: 0.25,
          minHeight: 78,
          '&.Mui-expanded': { minHeight: 78 },
          '& .MuiAccordionSummary-content': { my: 0 },
          '& .MuiAccordionSummary-content.Mui-expanded': { my: 0 },
        }}
      >
        {summary}
      </AccordionSummary>
      <AccordionDetails
        id={`ifood-item-${item.id}-groups`}
        sx={{ pt: 0, pb: 1.75, pl: { xs: 1, sm: 9 }, pr: 1 }}
      >
        <Stack spacing={1.25}>
          {groups.map((group) => {
            const options = Array.isArray(group.options) ? group.options : [];
            return (
              <Box
                key={group.id}
                sx={{
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: '#fff',
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  justifyContent="space-between"
                  spacing={0.75}
                  sx={{ px: 1.5, py: 1.1, bgcolor: '#f8fafc' }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={800}>{group.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {getGroupRuleLabel(group)} · {options.length} {options.length === 1 ? 'opção' : 'opções'}
                    </Typography>
                  </Box>
                  {!group.active && <Chip label="Grupo pausado" size="small" variant="outlined" />}
                </Stack>
                <Divider />
                {options.length ? (
                  <Stack divider={<Divider flexItem />}>
                    {options.map((option) => (
                      <Stack
                        key={option.id}
                        direction="row"
                        alignItems="center"
                        spacing={1.25}
                        sx={{ px: 1.5, py: 1 }}
                      >
                        <Avatar
                          src={option.image_url || undefined}
                          variant="rounded"
                          sx={{ width: 40, height: 40, flexShrink: 0, bgcolor: 'grey.100', color: 'text.secondary' }}
                        >
                          <RestaurantMenuOutlined fontSize="small" />
                        </Avatar>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" fontWeight={700} noWrap>{option.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {Number(option.price || 0) > 0 ? `+ ${money(option.price)}` : 'Sem acréscimo'}
                          </Typography>
                        </Box>
                        <Chip
                          label={option.active ? 'Disponível' : 'Indisponível'}
                          color={option.active ? 'success' : 'default'}
                          variant="outlined"
                          size="small"
                          sx={{ flexShrink: 0 }}
                        />
                      </Stack>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 1.25 }}>
                    Este grupo não possui opções cadastradas no iFood.
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const IfoodCatalogImportDialog = ({ open, onClose, merchantId, onImported }) => {
  const [preview, setPreview] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deactivateMissing, setDeactivateMissing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  const items = useMemo(
    () => (preview?.categories || []).flatMap((category) => (
      (category.items || []).map((item) => ({ ...item, categoryName: category.name }))
    )),
    [preview]
  );
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const loadPreview = useCallback(async () => {
    if (!merchantId) return;
    setLoading(true);
    try {
      const data = await previewIfoodCatalog(merchantId);
      setPreview(data);
      setSelectedIds(
        (data?.categories || []).flatMap((category) => (
          (category.items || [])
            .filter((item) => item.sync_status !== 'UNCHANGED')
            .map((item) => String(item.id))
        ))
      );
      setDeactivateMissing(false);
    } catch (error) {
      toast.error(getIfoodErrorMessage(error, 'Não foi possível consultar o cardápio no iFood.'));
    } finally {
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => {
    if (open) loadPreview();
    if (!open) {
      setPreview(null);
      setSelectedIds([]);
      setDeactivateMissing(false);
    }
  }, [open, loadPreview]);

  const toggleItem = (itemId) => {
    const id = String(itemId);
    setSelectedIds((current) => (
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id]
    ));
  };

  const toggleCategory = (category) => {
    const ids = (category.items || []).map((item) => String(item.id));
    const allSelected = ids.length > 0 && ids.every((id) => selectedSet.has(id));
    setSelectedIds((current) => {
      const next = new Set(current);
      ids.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
      return [...next];
    });
  };

  const handleImport = async () => {
    if (!selectedIds.length && !deactivateMissing) {
      toast.info('Selecione ao menos um item para trazer ao One Menu.');
      return;
    }
    setImporting(true);
    try {
      const result = await importIfoodCatalog(merchantId, {
        itemIds: selectedIds,
        deactivateMissing,
      });
      const changed = Number(result?.items_created || 0) + Number(result?.items_updated || 0);
      toast.success(`${changed} item(ns) sincronizado(s) com o One Menu.`);
      await onImported?.(result);
      onClose?.();
    } catch (error) {
      toast.error(getIfoodErrorMessage(error, 'Não foi possível trazer os itens do iFood.'));
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onClose={importing ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 7 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <RestaurantMenuOutlined sx={{ color: IFOOD_RED }} />
          <Box>
            <Typography variant="h6" fontWeight={900}>Trazer cardápio do iFood</Typography>
            <Typography variant="body2" color="text.secondary">
              Escolha o que deseja criar ou atualizar no One Menu.
            </Typography>
          </Box>
        </Stack>
        <IconButton
          aria-label="Fechar"
          onClick={onClose}
          disabled={importing}
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ minHeight: 360 }}>
        {loading ? (
          <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ minHeight: 300 }}>
            <CircularProgress sx={{ color: IFOOD_RED }} />
            <Typography color="text.secondary">Consultando o cardápio mais recente no iFood...</Typography>
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={`${preview?.summary?.new_items_count || 0} novos`} color="success" size="small" />
                <Chip label={`${preview?.summary?.updated_items_count || 0} com alterações`} color="warning" size="small" />
                <Chip label={`${preview?.summary?.unchanged_items_count || 0} sem alterações`} size="small" />
              </Stack>
              <Button startIcon={<Refresh />} onClick={loadPreview} disabled={loading || importing}>
                Consultar novamente
              </Button>
            </Stack>

            {!items.length && (
              <Alert severity="info">Nenhum item foi encontrado no cardápio desta loja do iFood.</Alert>
            )}

            {(preview?.categories || []).map((category) => {
              const categoryIds = (category.items || []).map((item) => String(item.id));
              const selectedCount = categoryIds.filter((id) => selectedSet.has(id)).length;
              return (
                <Accordion key={category.id} defaultExpanded={categoryIds.some((id) => selectedSet.has(id))} disableGutters>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Checkbox
                      checked={categoryIds.length > 0 && selectedCount === categoryIds.length}
                      indeterminate={selectedCount > 0 && selectedCount < categoryIds.length}
                      onClick={(event) => event.stopPropagation()}
                      onChange={() => toggleCategory(category)}
                      inputProps={{ 'aria-label': `Selecionar categoria ${category.name}` }}
                    />
                    <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%" pr={1}>
                      <Typography fontWeight={800}>{category.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {selectedCount} de {categoryIds.length}
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Stack divider={<Divider flexItem />}>
                      {(category.items || []).map((item) => (
                        <IfoodImportItem
                          key={item.id}
                          item={item}
                          selected={selectedSet.has(String(item.id))}
                          onToggle={toggleItem}
                        />
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              );
            })}

            {(preview?.missing_items || []).length > 0 && (
              <Alert severity="warning" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                <Typography fontWeight={800} gutterBottom>
                  {preview.missing_items.length} item(ns) importado(s) não existem mais no iFood
                </Typography>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={deactivateMissing}
                      onChange={(event) => setDeactivateMissing(event.target.checked)}
                    />
                  )}
                  label="Desativar esses itens no One Menu"
                />
                <Typography variant="caption" display="block">
                  Eles não serão excluídos e poderão ser reativados depois.
                </Typography>
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={importing}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={loading || importing || (!selectedIds.length && !deactivateMissing)}
          startIcon={importing ? <CircularProgress size={18} color="inherit" /> : <RestaurantMenuOutlined />}
          sx={{ bgcolor: IFOOD_RED, fontWeight: 800, '&:hover': { bgcolor: '#C91524' } }}
        >
          {importing ? 'Sincronizando...' : `Trazer ${selectedIds.length || ''} item(ns)`}
        </Button>
      </DialogActions>
      {importing && (
        <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(255,255,255,.72)', zIndex: 10, display: 'grid', placeItems: 'center' }}>
          <Stack alignItems="center" spacing={2}>
            <CircularProgress sx={{ color: IFOOD_RED }} />
            <Typography fontWeight={800}>Salvando os itens no One Menu...</Typography>
          </Stack>
        </Box>
      )}
    </Dialog>
  );
};

export default IfoodCatalogImportDialog;
